import React, { useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'



const dailySchedule = [
  { time: "4:00", activity: "Wake up" },
  { time: "4:45", activity: "Sitting meditation, chanting" },
  { time: "6:00", activity: "Exercise" },
  { time: "7:00", activity: "Breakfast" },
  { time: "8:30", activity: "Working meditation (garden, cleaning...)" },
  { time: "9:45", activity: "Dharma talk" },
  { time: "11:30", activity: "Walking meditation" },
  { time: "12:00", activity: "Lunch" },
  { time: "14:45", activity: "Dharma sharing (English/Vietnamese)" },
  { time: "16:30", activity: "Exercise/Rest" },
  { time: "17:30", activity: "Evening chanting" },
  { time: "18:30", activity: "Light dinner" },
  { time: "19:30", activity: "Personal practice/Evening activities" },
  { time: "21:00", activity: "Noble silence (until after breakfast next day)" }
]

export default function MonasteryGuidelines() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  
  const images = [
    "https://media.vov.vn/sites/default/files/styles/large/public/2023-07/tu_vien_tung_lam_3.png.jpg",
    "https://www.sgtiepthi.vn/wp-content/uploads/2022/06/Tu-vien-Vinh-Nghiem-9.jpg",
    "https://cdn3.ivivu.com/2015/04/tu-vien-tren-cao-an-tuong-ivivu-5.jpg",
    // Add more image URLs
  ];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length)
  }

  const previousImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length)
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-semibold mb-8">Monastery Guidelines</h1>

      {/* Daily Schedule */}
      <section className="mb-12">
        <h2 className="text-2xl font-medium mb-6">Daily Schedule</h2>
        <div className=" rounded-lg p-6">
          {dailySchedule.map((item) => (
            <div key={item.time} className="flex   py-2 ">
              <span className="w-20 font-medium">{item.time}</span>
              <span>{item.activity}</span>
            </div>
          ))}
        </div>
      </section>

      {/* What to Bring */}
      <section className="mb-12">
        <h2 className="text-2xl font-medium mb-6">What to Bring</h2>
        <ul className="list-disc list-inside space-y-2">
          <li>Loose, modest clothing in dark colors</li>
          <li>Personal hygiene items (toothbrush, towel, etc.)</li>
          <li>Meditation cushion (if you have one)</li>
          <li>Umbrella or rain gear</li>
          <li>Flashlight</li>
          <li>Alarm clock (non-digital preferred)</li>
        </ul>
      </section>

      {/* Image Gallery */}
      <section className="mb-12">
        <div className="relative">
          <img
            src={images[currentImageIndex]}
            alt={`Monastery view ${currentImageIndex + 1}`}
            className="w-full h-[400px] object-cover rounded-lg"
          />
          <button
            onClick={previousImage}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 p-2 rounded-full text-white hover:bg-black/70"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 p-2 rounded-full text-white hover:bg-black/70"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
        <div className="flex gap-2 mt-4 overflow-x-auto p-4">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`flex-shrink-0 w-21 h-21 rounded-md overflow-hidden ${
                index === currentImageIndex ? 'ring-2 ring-blue-500' : ''
              }`}
            >
              <img
                src={images[index]}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      </section>

      {/* Important Rules */}
      <section className="mb-12">
        <h2 className="text-2xl font-medium mb-6">Important Rules</h2>
        <div className="space-y-4">
          <div className="p-6 rounded-lg">
            <h3 className="font-medium mb-2">Arrival and Departure</h3>
            <ul className="list-disc list-inside space-y-2">
              <li>Check-in time: 2:00 PM - 4:00 PM</li>
              <li>Please arrive and depart only on scheduled days</li>
              <li>Minimum stay is 2 nights</li>
            </ul>
          </div>

          <div className="p-6 rounded-lg">
            <h3 className="font-medium mb-2">During Your Stay</h3>
            <ul className="list-disc list-inside space-y-2">
              <li>Observe noble silence from 9:30 PM to breakfast</li>
              <li>Participate in all scheduled activities</li>
              <li>Practice mindfulness in all daily activities</li>
              <li>Respect meditation spaces and other practitioners</li>
            </ul>
          </div>

          <div className="p-6 rounded-lg">
            <h3 className="font-medium mb-2">General Conduct</h3>
            <ul className="list-disc list-inside space-y-2">
              <li>Dress modestly and appropriately</li>
              <li>Keep electronic devices turned off</li>
              <li>Follow vegetarian diet during your stay</li>
              <li>Maintain cleanliness in all areas</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Offerings */}
      <section className="mb-12">
        <h2 className="text-2xl font-medium mb-6">Offerings</h2>
        <p className="text-gray-700 mb-4">
          The monastery operates on the principle of dana (generous giving). While there is no fixed fee,
          your contributions help maintain the facilities and support the community.
        </p>
        <div className="p-6 rounded-lg">
          <h3 className="font-medium mb-2">Suggested Contributions</h3>
          <ul className="list-disc list-inside space-y-2">
            <li>Short stay (2-3 days): $30-50 per day</li>
            <li>Extended stay (1 week or more): $25-40 per day</li>
            <li>Special retreats: See specific retreat information</li>
          </ul>
        </div>
      </section>
    </div>
  )
}